import {
  ApiShoppingCartsPostRequest,
  ApiShoppingCartsItemsPutRequest,
  ShoppingCartDto,
  ApiShoppingCartsGuidGetRequest,
  ApiShoppingCartsPriceListPatchRequest,
  ApiShoppingCartsShoppingCartItemIdDeleteRequest,
  ApiShoppingCartsGuidNamePatchRequest,
  PriceProductVm,
  ApiPricesPriceListIdPostRequest,
  ShoppingCartNameUpdatedDto,
  ApiShoppingCartsShoppingCartItemIdAdditionalInformationPatchRequest,
  ShoppingCartItemAdditionalInformationUpdatedDto,
  ApiShoppingCartsRequestOrderApprovalPostRequest,
  ApiShoppingCartsRequestQuotationPatchRequest,
  ApiShoppingCartsCancelQuotationRequestGuidPatchRequest,
  ApiShoppingCartsActivateGuidPatchRequest,
  ShoppingCartUpdatedDto,
  ApiShoppingCartsGuidRejectQuotePatchRequest,
  ApiShoppingCartsGuidAttachmentAttachmentIdDeleteRequest,
  ApiShoppingCartsGuidAttachmentTypePostRequest,
  AttachmentCreatedDto,
  ShoppingCartAttachmentTypes,
  SalesChannelDto,
  ApiShoppingCartsPlaceOrderWithoutApprovalPostRequest,
  QuoteDto,
  ApiShoppingCartsManageQuotesIdGetRequest,
  ApiShoppingCartsGuidRejectApprovalPatchRequest,
  ShoppingCartStateTypes,
  QuoteStateTypes,
  ApiShoppingCartsApproveQuotePatchRequest,
  ApiShoppingCartsGuidPlaceOrderFromWaitingForApprovalCommandPatchRequest,
  TermsOfDeliveryDto,
  ApiShoppingCartsTermsOfDeliveriesGetRequest,
  ApiShoppingCartsGuidSendQuoteWithoutApprovalPostRequest,
  ApiShoppingCartsGuidRequestQuoteApprovalPostRequest,
  ShoppingCartItemSoftwarePurchaseEmailUpdatedDto,
  ApiShoppingCartsShoppingCartItemIdSoftwarePurchaseEmailPatchRequest
} from "api";
import { RequestState, createInitialRequest } from "framework/state/requestState";
import { handleRequestActions } from "framework/state/genericAsyncRequest";
import { shoppingCartSagas } from "../sagas/shoppingCartSagas";
import { produce } from "immer";
import { AppState } from "../../../setup/appRootReducer";
import { quickPriceCheckSagas } from "../../quickPriceCheck/sagas/quickPriceCheckSagas";
import { ShoppingCartActions, ShoppingCartActionTypes } from "../actions/shoppingCartActions";
import { RequestStatus } from "framework/state/requestStatus";
import { ApiPricesPostRequest } from "../../../api/apis/PricesApi";
import { createSelector } from "reselect";
import { ExtendedQuoteDto, QuoteShoppingCartItemDto } from "../../../models/quote/quoteDto";
import { formatCurrency } from "utilities/currencyUtils";
import { QuoteShoppingCartDto } from "models/quote/quoteDto";

interface Requests {
  addOrUpdateItemsInShoppingCart: RequestState<void, ApiShoppingCartsItemsPutRequest>;
  createShoppingCart: RequestState<ShoppingCartUpdatedDto, ApiShoppingCartsPostRequest>;
  getShoppingCartById: RequestState<ShoppingCartDto, ApiShoppingCartsGuidGetRequest>;
  changeShoppingCartPriceList: RequestState<void, ApiShoppingCartsPriceListPatchRequest>;
  deleteShoppingCartItem: RequestState<void, ApiShoppingCartsShoppingCartItemIdDeleteRequest>;
  updateShoppingCartName: RequestState<
    ShoppingCartNameUpdatedDto,
    ApiShoppingCartsGuidNamePatchRequest
  >;
  getSinglePriceProductFromOrderingCode: RequestState<
    PriceProductVm,
    ApiPricesPriceListIdPostRequest
  >;
  getSinglePriceProductFromOrderingCodeWithoutPriceListId: RequestState<
    PriceProductVm,
    ApiPricesPostRequest
  >;
  updateShoppingCartItemAdditionalInformation: RequestState<
    ShoppingCartItemAdditionalInformationUpdatedDto,
    ApiShoppingCartsShoppingCartItemIdAdditionalInformationPatchRequest
  >;
  updateShoppingCartItemSoftwarePurchaseEmail: RequestState<
    ShoppingCartItemSoftwarePurchaseEmailUpdatedDto,
    ApiShoppingCartsShoppingCartItemIdSoftwarePurchaseEmailPatchRequest
  >;
  requestOrderApproval: RequestState<void, ApiShoppingCartsRequestOrderApprovalPostRequest>;
  requestQuotation: RequestState<void, ApiShoppingCartsRequestQuotationPatchRequest>;
  cancelQuotationRequest: RequestState<
    void,
    ApiShoppingCartsCancelQuotationRequestGuidPatchRequest
  >;
  activateShoppingCart: RequestState<
    ShoppingCartUpdatedDto,
    ApiShoppingCartsActivateGuidPatchRequest
  >;
  rejectQuote: RequestState<void, ApiShoppingCartsGuidRejectQuotePatchRequest>;
  uploadAttachment: RequestState<
    AttachmentCreatedDto,
    ApiShoppingCartsGuidAttachmentTypePostRequest
  >;
  deleteAttachment: RequestState<string, ApiShoppingCartsGuidAttachmentAttachmentIdDeleteRequest>;
  getSalesChannels: RequestState<Array<SalesChannelDto>, undefined>;
  placeOrderWithoutApproval: RequestState<
    void,
    ApiShoppingCartsPlaceOrderWithoutApprovalPostRequest
  >;
  getQuoteById: RequestState<QuoteDto, ApiShoppingCartsManageQuotesIdGetRequest>;
  requestQuoteApproval: RequestState<number, ApiShoppingCartsGuidRequestQuoteApprovalPostRequest>;
  rejectApproval: RequestState<void, ApiShoppingCartsGuidRejectApprovalPatchRequest>;
  approveOrderOrQuote: RequestState<void, ApiShoppingCartsApproveQuotePatchRequest>;
  sendQuoteWithoutApproval: RequestState<
    number,
    ApiShoppingCartsGuidSendQuoteWithoutApprovalPostRequest
  >;
  placeOrder: RequestState<
    void,
    ApiShoppingCartsGuidPlaceOrderFromWaitingForApprovalCommandPatchRequest
  >;
  getTermsOfDeliveries: RequestState<
    TermsOfDeliveryDto[],
    ApiShoppingCartsTermsOfDeliveriesGetRequest
  >;
}

export interface ShoppingCartState {
  requests: Requests;
  shoppingCart: QuoteShoppingCartDto | undefined;
  salesChannels: SalesChannelDto[] | undefined;
  priceProductVm: PriceProductVm | undefined;
  quote: ExtendedQuoteDto | undefined;
  termsOfDeliveries: TermsOfDeliveryDto[] | undefined;
}

const defaultState: ShoppingCartState = {
  shoppingCart: undefined,
  quote: undefined,
  priceProductVm: undefined,
  salesChannels: undefined,
  termsOfDeliveries: undefined,
  requests: {
    addOrUpdateItemsInShoppingCart: createInitialRequest(),
    createShoppingCart: createInitialRequest(),
    getShoppingCartById: createInitialRequest(),
    changeShoppingCartPriceList: createInitialRequest(),
    deleteShoppingCartItem: createInitialRequest(),
    updateShoppingCartName: createInitialRequest(),
    getSinglePriceProductFromOrderingCode: createInitialRequest(),
    updateShoppingCartItemAdditionalInformation: createInitialRequest(),
    updateShoppingCartItemSoftwarePurchaseEmail: createInitialRequest(),
    requestOrderApproval: createInitialRequest(),
    getSinglePriceProductFromOrderingCodeWithoutPriceListId: createInitialRequest(),
    requestQuotation: createInitialRequest(),
    cancelQuotationRequest: createInitialRequest(),
    activateShoppingCart: createInitialRequest(),
    rejectQuote: createInitialRequest(),
    uploadAttachment: createInitialRequest(),
    deleteAttachment: createInitialRequest(),
    getSalesChannels: createInitialRequest(),
    placeOrderWithoutApproval: createInitialRequest(),
    getQuoteById: createInitialRequest(),
    requestQuoteApproval: createInitialRequest(),
    rejectApproval: createInitialRequest(),
    approveOrderOrQuote: createInitialRequest(),
    sendQuoteWithoutApproval: createInitialRequest(),
    placeOrder: createInitialRequest(),
    getTermsOfDeliveries: createInitialRequest()
  }
};

export function shoppingCartReducer(
  state: ShoppingCartState = defaultState,
  action: ShoppingCartActions
): ShoppingCartState {
  state = handleRequestActions(state, "requests", action, [
    {
      actionTypes: shoppingCartSagas.uploadAttachment.actionTypes,
      key: "uploadAttachment"
    },
    {
      actionTypes: shoppingCartSagas.deleteAttachment.actionTypes,
      key: "deleteAttachment"
    },
    {
      actionTypes: shoppingCartSagas.addOrUpdateItemsInShoppingCart.actionTypes,
      key: "addOrUpdateItemsInShoppingCart"
    },
    {
      actionTypes: shoppingCartSagas.createShoppingCart.actionTypes,
      key: "createShoppingCart"
    },
    {
      actionTypes: shoppingCartSagas.getShoppingCartById.actionTypes,
      key: "getShoppingCartById"
    },
    {
      actionTypes: shoppingCartSagas.changeShoppingCartPriceList.actionTypes,
      key: "changeShoppingCartPriceList"
    },
    {
      actionTypes: shoppingCartSagas.deleteShoppingCartItem.actionTypes,
      key: "deleteShoppingCartItem"
    },
    {
      actionTypes: shoppingCartSagas.updateShoppingCartName.actionTypes,
      key: "updateShoppingCartName"
    },
    {
      actionTypes: quickPriceCheckSagas.getSinglePriceProductFromOrderingCode.actionTypes,
      key: "getSinglePriceProductFromOrderingCode"
    },
    {
      actionTypes:
        quickPriceCheckSagas.getSinglePriceProductFromOrderingCodeWithoutPriceListId.actionTypes,
      key: "getSinglePriceProductFromOrderingCodeWithoutPriceListId"
    },
    {
      actionTypes: shoppingCartSagas.updateShoppingCartItemAdditionalInformation.actionTypes,
      key: "updateShoppingCartItemAdditionalInformation"
    },
    {
      actionTypes: shoppingCartSagas.updateShoppingCartItemSoftwarePurchaseEmail.actionTypes,
      key: "updateShoppingCartItemSoftwarePurchaseEmail"
    },
    {
      actionTypes: shoppingCartSagas.requestOrderApproval.actionTypes,
      key: "requestOrderApproval"
    },
    {
      actionTypes: shoppingCartSagas.requestQuotation.actionTypes,
      key: "requestQuotation"
    },
    {
      actionTypes: shoppingCartSagas.cancelQuotationRequest.actionTypes,
      key: "cancelQuotationRequest"
    },
    {
      actionTypes: shoppingCartSagas.activateShoppingCart.actionTypes,
      key: "activateShoppingCart"
    },
    {
      actionTypes: shoppingCartSagas.rejectQuote.actionTypes,
      key: "rejectQuote"
    },
    {
      actionTypes: shoppingCartSagas.getSalesChannels.actionTypes,
      key: "getSalesChannels"
    },
    {
      actionTypes: shoppingCartSagas.placeOrderWithoutApproval.actionTypes,
      key: "placeOrderWithoutApproval"
    },
    {
      actionTypes: shoppingCartSagas.getQuoteById.actionTypes,
      key: "getQuoteById"
    },
    {
      actionTypes: shoppingCartSagas.requestQuoteApproval.actionTypes,
      key: "requestQuoteApproval"
    },
    {
      actionTypes: shoppingCartSagas.rejectApproval.actionTypes,
      key: "rejectApproval"
    },
    {
      actionTypes: shoppingCartSagas.approveOrderOrQuote.actionTypes,
      key: "approveOrderOrQuote"
    },
    {
      actionTypes: shoppingCartSagas.sendQuoteWithoutApproval.actionTypes,
      key: "sendQuoteWithoutApproval"
    },
    {
      actionTypes: shoppingCartSagas.placeOrder.actionTypes,
      key: "placeOrder"
    },
    {
      actionTypes: shoppingCartSagas.getTermsOfDeliveries.actionTypes,
      key: "getTermsOfDeliveries"
    }
  ]);

  if (shoppingCartSagas.getShoppingCartById.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart?.shoppingCartItems.some((x) => x.discountRate)) {
        const discountedItems = action.payload.shoppingCartItems.map((item) => {
          const matchedItem = state.shoppingCart?.shoppingCartItems.find((x) => x.id === item.id);
          if (matchedItem && matchedItem.discountRate && item.unitPrice && item.price) {
            return {
              ...item,
              unitQuotedPrice: Math.round(
                (parseInt(item.unitPrice.replace(/ /g, "")) * (100 - matchedItem.discountRate)) /
                  100
              ).toString(),
              quotedPrice: formatCurrency(
                Math.round(
                  (parseInt(item.price.replace(/ /g, "")) * (100 - matchedItem.discountRate)) / 100
                ),
                action.payload.priceListCurrency || ""
              ),
              discountRate: matchedItem.discountRate
            };
          }
          return item;
        });
        action.payload.shoppingCartItems = discountedItems;
      }
      draft.shoppingCart = action.payload;
    });
  } else if (shoppingCartSagas.getSalesChannels.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      draft.salesChannels = action.payload;
    });
  } else if (shoppingCartSagas.updateShoppingCartName.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart && draft.shoppingCart.guid === action.payload.guid) {
        draft.shoppingCart.name = action.payload.name;
        draft.shoppingCart.lastModified = action.payload.lastModified;
        draft.shoppingCart.lastModifiedBy = action.payload.lastModifiedBy;
      }
    });
  } else if (quickPriceCheckSagas.getSinglePriceProductFromOrderingCode.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      draft.priceProductVm = action.payload;
    });
  } else if (
    quickPriceCheckSagas.getSinglePriceProductFromOrderingCodeWithoutPriceListId.isCompletedAction(
      action
    )
  ) {
    state = produce(state, (draft) => {
      draft.priceProductVm = action.payload;
    });
  } else if (
    shoppingCartSagas.updateShoppingCartItemAdditionalInformation.isCompletedAction(action)
  ) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart && draft.shoppingCart.guid === action.payload.guid) {
        const id = draft.shoppingCart.shoppingCartItems.findIndex(
          (x) => x.id === action.payload.shoppingCartItemId
        );
        if (id > -1) {
          draft.shoppingCart.shoppingCartItems[id].additionalInformation =
            action.payload.additionalInformation;
          draft.shoppingCart.lastModified = action.payload.lastModified;
          draft.shoppingCart.lastModifiedBy = action.payload.lastModifiedBy;
        }
      }
    });
  } else if (
    shoppingCartSagas.updateShoppingCartItemSoftwarePurchaseEmail.isCompletedAction(action)
  ) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart && draft.shoppingCart.guid === action.payload.guid) {
        const id = draft.shoppingCart.shoppingCartItems.findIndex(
          (x) => x.id === action.payload.shoppingCartItemId
        );
        if (id > -1) {
          draft.shoppingCart.shoppingCartItems[id].softwarePurchaseEmail =
            action.payload.softwarePurchaseEmail;
          draft.shoppingCart.lastModified = action.payload.lastModified;
          draft.shoppingCart.lastModifiedBy = action.payload.lastModifiedBy;
        }
      }
    });
  } else if (shoppingCartSagas.requestQuotation.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      // to avoid flashing old cart in UI
      draft.shoppingCart = undefined;
    });
  } else if (shoppingCartSagas.requestOrderApproval.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      // to avoid flashing old cart in UI
      draft.shoppingCart = undefined;
    });
  } else if (shoppingCartSagas.createShoppingCart.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      // to avoid flashing old cart in UI
      draft.shoppingCart = undefined;
    });
  } else if (shoppingCartSagas.uploadAttachment.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      // to avoid flashing old cart in UI
      if (draft.shoppingCart) {
        if (
          action.payload.shoppingCartAttachmentTypeId ===
          ShoppingCartAttachmentTypes.CommercialInvoice
        ) {
          draft.shoppingCart.commercialInvoices.push(action.payload);
        } else {
          draft.shoppingCart.riskReviews.push(action.payload);
        }
      }
    });
  } else if (shoppingCartSagas.deleteAttachment.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      const identifier = JSON.parse(action.payload);
      if (draft.shoppingCart) {
        draft.shoppingCart.riskReviews = draft.shoppingCart.riskReviews.filter(
          (x) => x.identifier !== identifier
        );
        draft.shoppingCart.commercialInvoices = draft.shoppingCart.commercialInvoices.filter(
          (x) => x.identifier !== identifier
        );
      }
    });
  } else if (shoppingCartSagas.getQuoteById.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      const items: Array<QuoteShoppingCartItemDto> = [
        ...action.payload.shoppingCart.shoppingCartItems
      ];

      if (items !== undefined) {
        const shoppingCartItems: Array<QuoteShoppingCartItemDto> = items.map((item) => {
          if (item.unitQuotedPrice && item.unitPrice) {
            const unitQuotedPrice = parseInt(item.unitQuotedPrice.replace(/ /g, ""));
            const unitPrice = parseInt(item.unitPrice.replace(/ /g, ""));
            return {
              ...item,
              discountRate: 100 - Math.round((unitQuotedPrice * 100) / unitPrice)
            };
          } else {
            return { ...item, discountRate: 0 };
          }
        });
        const shoppingCart = { ...action.payload.shoppingCart, shoppingCartItems };
        draft.quote = { ...action.payload, shoppingCart };
      } else {
        draft.quote = action.payload;
      }
    });
  } else if (shoppingCartSagas.rejectApproval.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart) {
        if (draft.shoppingCart.stateId === ShoppingCartStateTypes.OrderWaitingForApproval) {
          draft.shoppingCart.stateId = ShoppingCartStateTypes.Rejected;
        } else {
          draft.shoppingCart.stateId = ShoppingCartStateTypes.WaitingForQuote;
        }
      }
      if (draft.quote) {
        draft.quote.stateId = QuoteStateTypes.WaitingForQuote;
      }
    });
  } else if (shoppingCartSagas.placeOrder.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart) {
        draft.shoppingCart.stateId = ShoppingCartStateTypes.OrderPlaced;
      }
    });
  } else if (shoppingCartSagas.placeOrderWithoutApproval.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      if (draft.shoppingCart) {
        draft.shoppingCart = undefined;
      }
    });
  } else if (shoppingCartSagas.getTermsOfDeliveries.isCompletedAction(action)) {
    state = produce(state, (draft) => {
      draft.termsOfDeliveries = action.payload;
    });
  }

  switch (action.type) {
    case ShoppingCartActionTypes.SetPriceProductVm:
      state = produce(state, (draft) => {
        draft.priceProductVm = action.priceProductVm;
      });
      break;
    case ShoppingCartActionTypes.SetShoppingCart:
      state = produce(state, (draft) => {
        draft.shoppingCart = undefined;
      });
      break;
    case ShoppingCartActionTypes.SetQuote:
      state = produce(state, (draft) => {
        draft.quote = undefined;
      });
      break;
    case ShoppingCartActionTypes.UpdateLineItem:
      state = produce(state, (draft) => {
        const cart = action.isQuote ? draft.quote?.shoppingCart : draft.shoppingCart;
        if (cart && cart.shoppingCartItems) {
          const id = cart.shoppingCartItems.findIndex(
            (x) => x.orderingCode === action.orderingCode
          );
          if (id !== undefined && id > -1) {
            const quantity = cart.shoppingCartItems[id].quantity ?? 1;
            const unitPrice = cart.shoppingCartItems[id].unitPrice ?? "0";
            const originalQuotedPrice = cart.shoppingCartItems[id].quotedPrice;
            let quotedPrice = originalQuotedPrice
              ? parseInt(originalQuotedPrice.replace(/ /g, ""))
              : null;

            let discountRate = cart.shoppingCartItems[id].discountRate;
            let unitQuotedPrice: number | null | undefined = parseInt(
              cart.shoppingCartItems[id].unitQuotedPrice ?? "0"
            );

            switch (action.infoType) {
              case "discountRate":
                discountRate = action.discountRate;
                unitQuotedPrice =
                  discountRate != null && discountRate !== 0
                    ? Math.round(
                        (parseInt(unitPrice.replace(/ /g, "")) * (100 - discountRate)) / 100
                      )
                    : null;
                break;
              case "unitQuotedPrice":
                unitQuotedPrice = action.unitQuotedPrice;
                discountRate =
                  unitQuotedPrice != null
                    ? 100 -
                      Math.round((unitQuotedPrice * 100) / parseInt(unitPrice.replace(/ /g, "")))
                    : null;
                break;
            }
            quotedPrice = unitQuotedPrice != null ? quantity * unitQuotedPrice : null;

            cart.shoppingCartItems[id] = {
              ...cart.shoppingCartItems[id],
              discountRate,
              unitQuotedPrice:
                unitQuotedPrice != null ? unitQuotedPrice.toString() : unitQuotedPrice,
              quotedPrice:
                quotedPrice !== null
                  ? formatCurrency(quotedPrice, cart.priceListCurrency || "")
                  : quotedPrice
            };
          }
        }
      });
      break;
  }

  return state;
}

export const getShoppingCart = (state: AppState): ShoppingCartDto | undefined =>
  state.shoppingCart.shoppingCart;

export const getChangeShoppingCartPriceListStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.changeShoppingCartPriceList.status;

export const getAddOrUpdateItemsInShoppingCartStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.addOrUpdateItemsInShoppingCart.status;

export const getGetShoppingCartByIdStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.getShoppingCartById.status;

export const getGetUpdateShoppingCartNameStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.updateShoppingCartName.status;

export const getPriceProductVm = (state: AppState): PriceProductVm | undefined =>
  state.shoppingCart.priceProductVm;

export const getQuote = (state: AppState): QuoteDto | undefined => state.shoppingCart.quote;

export const getSinglePriceProductStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.getSinglePriceProductFromOrderingCode.status;

export const getSinglePriceProductWithoutPriceListIdStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.getSinglePriceProductFromOrderingCodeWithoutPriceListId.status;

export const isSingleProductLoading = createSelector(
  getSinglePriceProductStatus,
  getSinglePriceProductWithoutPriceListIdStatus,
  (status1, status2) => {
    if (status1 === RequestStatus.Pending || status2 === RequestStatus.Pending) {
      return true;
    }
    return false;
  }
);

export const getUpdateShoppingCartItemAdditionalInformationStatus = (
  state: AppState
): RequestStatus => state.shoppingCart.requests.updateShoppingCartItemAdditionalInformation.status;
export const getUpdateShoppingCartItemSoftwarePurchaseEmailStatus = (
  state: AppState
): RequestStatus => state.shoppingCart.requests.updateShoppingCartItemSoftwarePurchaseEmail.status;
export const getRequestQuotationStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.requestQuotation.status;
export const getDeleteShoppingCartItemStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.deleteShoppingCartItem.status;
export const getRequestOrderApprovalStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.requestOrderApproval.status;
export const getCancelQuotationRequestStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.cancelQuotationRequest.status;
export const getActivateShoppingCartStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.activateShoppingCart.status;
export const getCreateShoppingCartStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.createShoppingCart.status;
export const getUploadAttachmentStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.uploadAttachment.status;
export const getDeleteAttachmentStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.deleteAttachment.status;
export const getSalesChannels = (state: AppState): SalesChannelDto[] | undefined =>
  state.shoppingCart.salesChannels;
export const getPlaceOrderWithoutApprovalStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.placeOrderWithoutApproval.status;
export const getRejectApprovalStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.rejectApproval.status;
export const getApproveOrderOrQuoteStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.approveOrderOrQuote.status;
export const getRequestQuoteApprovalRequestState = (
  state: AppState
): RequestState<number, ApiShoppingCartsGuidRequestQuoteApprovalPostRequest> =>
  state.shoppingCart.requests.requestQuoteApproval;
export const getSendQuoteWithoutApprovalRequestState = (
  state: AppState
): RequestState<number, ApiShoppingCartsGuidSendQuoteWithoutApprovalPostRequest> =>
  state.shoppingCart.requests.sendQuoteWithoutApproval;
export const getPlaceOrderStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.placeOrder.status;
export const getTermsOfDeliveries = (state: AppState): TermsOfDeliveryDto[] | undefined =>
  state.shoppingCart.termsOfDeliveries;
/* export const getTermsOfDeliveriesStatus = (state: AppState): RequestStatus =>
  state.shoppingCart.requests.getTermsOfDeliveries.status; */
